import { ACCESS_TOKENS_ENABLED } from '@forecastify/shared';
import type { MessageKey } from '../../i18n';
import type { AccountSectionId } from '../account/account-screen';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 17 Oct 2026 · Every heading inside the settings, for the settings palette. │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The product owner: the settings search finds *"all headers of all settings"*. A section's name
 * is its rail row; THESE are the headings inside it — card titles and tabs — so *Jelszó*,
 * *Kétlépcsős azonosítás* or *Prioritások* each open the section they sit in. The entities the
 * sections hold come from the server (`GET /search/settings`).
 *
 * ⚠️ **Kept beside the sections and checked against them** — `settings-search.test.ts` reads every
 * `SettingsCard` title in the section files and fails when one is missing here, so a new card is
 * findable the day it ships.
 */

/** The workspace's sections, by `SETTINGS_SECTIONS` key. */
export const WORKSPACE_HEADINGS: Readonly<Record<string, readonly MessageKey[]>> = {
  general: [
    'settings.general.identity',
    'settings.general.defaults',
    'settings.general.notifications',
  ],
  tasks: ['settings.statuses.title', 'settings.priorities.title'],
  skills: ['settings.skills.title', 'settings.skills.categories', 'settings.skills.levels'],
  schedules: ['settings.schedules.tab.templates', 'settings.schedules.tab.assignments'],
  rates: ['settings.rates.tab.templates', 'settings.rates.tab.assignments'],
  leave: ['settings.leave.tab.types', 'settings.leave.tab.quotas'],
  worklog: ['settings.worklog.title'],
  data: ['settings.export.title', 'settings.delete.title'],
  billing: ['billing.plan.title', 'billing.seats.title', 'billing.details.title'],
};

/** The person's own sections, by `ACCOUNT_NAV` id. */
export const ACCOUNT_HEADINGS: Readonly<Record<AccountSectionId, readonly MessageKey[]>> = {
  profile: ['account.avatar.title', 'account.basics.title', 'account.field.email'],
  skills: [],
  security: [
    'account.password.title',
    'account.twofa.title',
    'account.passkeys.title',
    'account.sessions.title',
  ],
  preferences: ['account.prefs.title'],
  theme: ['account.theme.title'],
  notifications: ['account.notif.title', 'account.notif.reminder'],
  integrations: [
    'account.google.title',
    /* ⚠️ Not searchable while the card is off — `ACCESS_TOKENS_ENABLED`. */
    ...(ACCESS_TOKENS_ENABLED ? (['account.tokens.title'] as const) : []),
    'account.connections.title',
    'account.apps.title',
  ],
};
